// src/BookingTracker.js - where a booking has got to, at a glance
import React from 'react';
import { Box, Typography, Tooltip } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { brand, ink, surface } from './designTokens';

// The happy path, in order. Everything else is an ending rather than a step,
// so it is drawn differently instead of being squeezed onto the line.
const STEPS = [
  { key: 'Requested', label: 'Requested', hint: 'The client asked for this time' },
  { key: 'Confirmed', label: 'Confirmed', hint: 'The stylist accepted and payment was taken' },
  { key: 'Completed', label: 'Completed', hint: 'The session happened' },
];

// Statuses that end a booking without finishing it. Each says what actually
// happened, because "not completed" covers three very different situations and
// only one of them needs chasing.
const ENDINGS = {
  Declined: 'The stylist turned this down',
  Expired: 'Nobody answered in time, so it lapsed',
  'Payment Failed': "The client's payment did not go through",
  Cancelled: 'Cancelled',
};

export default function BookingTracker({ status }) {
  const ending = ENDINGS[status];
  const reachedIndex = STEPS.findIndex(s => s.key === status);

  // An ending still shows how far it got: a declined booking was requested,
  // and a failed payment means a stylist had already accepted.
  const progressed = ending
    ? status === 'Payment Failed'
      ? 1
      : 0
    : reachedIndex;

  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0, py: 1 }}>
      {STEPS.map((step, i) => {
        const done = i <= progressed;
        const isCurrent = i === progressed && !ending;
        const stoppedHere = !!ending && i === progressed + 1;

        return (
          <React.Fragment key={step.key}>
            {i > 0 && (
              <Box
                sx={{
                  flex: 1,
                  height: 2,
                  mt: '11px',
                  bgcolor: done ? brand.gold : surface.line,
                }}
              />
            )}
            <Tooltip title={stoppedHere ? ending : step.hint} arrow>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 86 }}>
                <Box
                  sx={{
                    width: 24,
                    height: 24,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: stoppedHere ? 'error.main' : done ? brand.gold : '#fff',
                    border: done || stoppedHere ? 'none' : `2px solid ${surface.line}`,
                    color: '#fff',
                  }}
                >
                  {stoppedHere ? (
                    <CloseIcon sx={{ fontSize: 15 }} />
                  ) : done ? (
                    <CheckIcon sx={{ fontSize: 15 }} />
                  ) : null}
                </Box>
                <Typography
                  variant="caption"
                  sx={{
                    mt: 0.5,
                    textAlign: 'center',
                    color: stoppedHere ? 'error.main' : done ? ink[900] : ink[500],
                    fontWeight: isCurrent || stoppedHere ? 600 : 400,
                  }}
                >
                  {stoppedHere ? status : step.label}
                </Typography>
              </Box>
            </Tooltip>
          </React.Fragment>
        );
      })}
    </Box>
  );
}
